import PropTypes from 'prop-types';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
// hooks
import useSettings from 'src/hooks/useSettings';
import useCollapseDrawer from 'src/hooks/useCollapseDrawer';
// universe
import {
  MOTION,
  RADIUS,
  UNIVERSE,
  UNIVERSE_FONT,
  accentOf,
  accentTint,
  fieldLineOf,
  focusRingSx,
  hairlineOf,
  sunkenOf
} from 'src/theme/universe/brand';

// ----------------------------------------------------------------------
// THE SIDEBAR'S WIDTH (DESIGN.md §12): the full sidebar with its labels, or
// the narrow one with icons only. Each choice is a tile that draws what it
// does — the night of the rail (§12: the rail is `universe.night` in both
// modes) beside the page — and names it, so it is read before it is
// pressed. The chosen tile wears the accent edge and its wash. What a press
// does is unchanged: it flips `viewNavbar` and the drawer's collapse.

const OPTIONS = [
  { value: 'vertical', label: 'Full', collapsed: false },
  { value: 'mini', label: 'Compact', collapsed: true }
];

function Miniature({ collapsed }) {
  const theme = useTheme();
  const light = alpha(UNIVERSE.white, 0.42);
  return (
    <Box
      aria-hidden
      sx={{
        width: 1,
        height: 56,
        display: 'flex',
        gap: 0.75,
        p: 0.75,
        boxSizing: 'border-box',
        borderRadius: `${RADIUS.small}px`,
        bgcolor: sunkenOf(theme)
      }}
    >
      {/* the rail: the night, with its items */}
      <Box
        sx={{
          flex: 'none',
          width: collapsed ? 14 : 34,
          borderRadius: '4px',
          bgcolor: UNIVERSE.night,
          display: 'flex',
          flexDirection: 'column',
          alignItems: collapsed ? 'center' : 'flex-start',
          gap: '4px',
          px: collapsed ? 0 : '5px',
          pt: '6px'
        }}
      >
        <Box
          sx={{
            width: collapsed ? 6 : 14,
            height: 4,
            borderRadius: '2px',
            backgroundImage: UNIVERSE.gradient
          }}
        />
        {[0, 1, 2].map((item) => (
          <Box
            key={item}
            sx={{
              width: collapsed ? 6 : 20 - item * 3,
              height: 3,
              borderRadius: '2px',
              bgcolor: light
            }}
          />
        ))}
      </Box>
      {/* the page: a title line and two rows of paper */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          pt: '4px'
        }}
      >
        <Box
          sx={{
            width: '46%',
            height: 4,
            borderRadius: '2px',
            bgcolor: fieldLineOf(theme)
          }}
        />
        <Box
          sx={{
            flex: 1,
            borderRadius: '3px',
            bgcolor: 'background.paper',
            boxShadow: `inset 0 0 0 1px ${hairlineOf(theme)}`
          }}
        />
      </Box>
    </Box>
  );
}

Miniature.propTypes = { collapsed: PropTypes.bool };

export default function LayoutOptions() {
  const theme = useTheme();
  const { viewNavbar, onToggleViewNavbar } = useSettings();
  const { onToggleCollapse } = useCollapseDrawer();
  const current = viewNavbar ? 'mini' : 'vertical';
  const accent = accentOf(theme);

  const choose = (value) => {
    if (value === current) return;
    onToggleViewNavbar(!viewNavbar);
    onToggleCollapse();
  };

  return (
    <Box
      role="radiogroup"
      aria-labelledby="settings-layout-label"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 1.25
      }}
    >
      {OPTIONS.map((option) => {
        const on = current === option.value;
        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={`${option.label} sidebar`}
            onClick={() => choose(option.value)}
            sx={{
              m: 0,
              p: 0.75,
              pb: 1,
              border: 0,
              cursor: on ? 'default' : 'pointer',
              textAlign: 'center',
              borderRadius: `${RADIUS.panel}px`,
              bgcolor: on ? accentTint(theme, 'selected') : 'background.paper',
              boxShadow: on
                ? `inset 0 0 0 2px ${accent}`
                : `inset 0 0 0 1px ${fieldLineOf(theme)}`,
              transition: theme.transitions.create(
                ['background-color', 'box-shadow'],
                { duration: MOTION.fast }
              ),
              '&:hover': on
                ? {}
                : {
                    bgcolor: accentTint(theme, 'hover'),
                    boxShadow: `inset 0 0 0 1px ${alpha(accent, 0.5)}`
                  },
              '&:focus-visible': focusRingSx(theme)
            }}
          >
            <Miniature collapsed={option.collapsed} />
            <Typography
              component="span"
              sx={{
                display: 'block',
                mt: 0.875,
                fontFamily: UNIVERSE_FONT,
                fontSize: 13,
                fontWeight: on ? 600 : 500,
                lineHeight: 1.2,
                color: on ? accent : 'text.secondary'
              }}
            >
              {option.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
